<template>
  <div class="form-item voucher-input-container">
    <label class="form__label">
      <span
        class="form__label-text__success"
        v-if="discount.isValid"
        data-testid="success-voucher-message"
        >Voucher applied successfully!</span
      >
      <span class="form__label-text__title" v-else-if="discount.inputRevealed"
        >Voucher code</span
      >
    </label>
    <div v-if="discount.isValid">
      <div class="discount-applied-display">
        <div class="content">
          <section>
            <h6 class="title">{{ discount.code }}</h6>
            <span>{{ discount.description }}</span>
          </section>

          <button class="btn plain-btn" @click="clear">
            <Trash />
          </button>
        </div>
      </div>

      <label class="form__label">
        <a @click="clear"
          ><span class="form__label-text__link">Change voucher code</span></a
        >
      </label>
    </div>
    <div v-else>
      <div class="input-wrapper" v-if="discount.inputRevealed">
        <div class="form__input">
          <input
            id="voucher-code"
            data-testid="voucher-code"
            class="voucher-input"
            name="voucher-code"
            :value="value"
            @input="update"
          />
        </div>
        <button
          class="btn apply-discount-button"
          @click="apply"
          :disabled="disabled"
          data-testid="apply-voucher-button"
        >
          <span v-if="!discount.isValidating"> Apply </span>
          <i
            v-else
            class="fa-solid fa-spinner-third fa-spin"
            data-testid="voucher-loading-animation"
          ></i>
        </button>
      </div>
      <a
        class="voucher-activation"
        data-testid="voucher-reveal-button"
        v-else
        @click="revealInput"
      >
        <span class="form__label-text__link">Have a voucher?</span>
      </a>
    </div>
    <FormError
      class="form__error"
      data-testid="discount-error"
      v-if="discount.errorMsg.length > 0"
      :msg="discount.errorMsg"
    />
    <span class="form__error" v-if="showTimeoutMessage">
      Please try again in
      {{ new Date(remainingTimeout).getSeconds().toString() }} seconds.
    </span>
  </div>
</template>

<script>
import Trash from "@/assets/svg/icon-trash.svg";
import FormError from "@/components/forms/FormError.vue";
import { mapGetters } from "vuex";
import { trackGoogleEvent } from "@/mixins/eventTracking";

export default {
  name: "DiscountActivation",
  data() {
    return {
      isDisabled: false,
      startTimeMs: 0,
      timeoutDurationMs: 30000,
      remainingTimeout: 0,
    };
  },
  components: {
    FormError,
    Trash,
  },

  created() {
    let defaultVoucher = this.getDefaultVoucher;
    let currentCode = this.$store.state.discount.code;
    if (
      defaultVoucher &&
      !this.$store.state.discount.isValid &&
      (currentCode === "" || currentCode === defaultVoucher) &&
      this.$store.getters.selectedProductPriceIds.length !== 0
    ) {
      this.$store.commit("SET_DISCOUNT_STATE", {
        inputRevealed: true,
        code: defaultVoucher || "", // set code here
      });

      if (this.getAutoApplyDefaultVoucher) {
        //Push App state to Google for audience tracking
        trackGoogleEvent(
          "auto-apply-default-voucher-codes-test",
          "Feature Flag",
          "auto-apply-default-voucher-codes-test",
        );
        this.apply();
      } else {
        trackGoogleEvent(
          "auto-apply-default-voucher-codes-control",
          "Feature Flag",
          "auto-apply-default-voucher-codes-control",
        );
      }
    }
  },
  props: {
    value: String,
  },
  computed: {
    ...mapGetters({
      shouldShowVouchers: "shouldShowVouchers",
      getDefaultVoucher: "getDefaultVoucher",
      getAutoApplyDefaultVoucher: "getAutoApplyDefaultVoucher",
    }),
    disabled: {
      get() {
        return this.value.trim().length <= 0 || this.isDisabled;
      },
    },
    discount: {
      get() {
        return this.$store.state.discount;
      },
    },
    packagePrice: {
      get() {
        return this.$store.state.packagePrice;
      },
    },
    showTimeoutMessage() {
      return (
        !(this.remainingTimeout < 1000) &&
        this.discount.inputRevealed &&
        !this.discount.isValid &&
        !this.discount.isValidating
      );
    },
  },
  methods: {
    update(e) {
      this.$emit("input", e.target.value);
    },
    apply() {
      this.$emit("apply");
      this.disableApply();
    },
    clear() {
      this.$emit("clear");
    },
    revealInput() {
      if (!this.shouldShowVouchers) {
        return; // vouchers not allowed
      }

      const defaultVoucher = this.getDefaultVoucher;

      this.$store.commit("SET_DISCOUNT_STATE", {
        inputRevealed: true,
        code: defaultVoucher || "", // set code here
      });
    },
    disableApply() {
      this.$data.isDisabled = true;
      this.$data.startTimeMs = new Date().getTime();

      let defaultVoucher = this.getDefaultVoucher;
      let currentCode = this.$store.state.discount.code;
      if (currentCode !== defaultVoucher) {
        const interval = setInterval(() => {
          this.$data.remainingTimeout =
            this.timeoutDurationMs - (new Date().getTime() - this.startTimeMs);
        }, 1000);

        setTimeout(() => {
          this.$data.isDisabled = false;
          clearInterval(interval);
        }, this.timeoutDurationMs);
      } else {
        this.$data.isDisabled = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.voucher-input-container {
  display: grid;
  margin: 1rem 0 3rem 0 !important;

  span {
    font-size: 18px;
    font-weight: 400;
  }

  .input-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    height: 100%;

    .form__input {
      margin: 0;
      height: 100%;

      input {
        height: 100%;
      }
    }

    .btn {
      margin: 0;
      background: $brand-orange;
      padding: 0.5em 1em;

      &:disabled {
        opacity: 0.5;
      }

      &:hover {
        box-shadow: $brand-box-shadow;
      }
    }

    @include media("<medium") {
      flex-direction: column;

      .btn {
        width: 100%;
      }
    }
  }
}

.voucher-input {
  appearance: none;
  outline: none;
  border: none;
  display: block;
  padding: 0;
  width: 100%;
}

.voucher-activation {
  display: flex;
  justify-content: center;
}

label.form__label {
  text-align: left;
  margin-top: 0.2em;

  a {
    display: flex;
  }
}

.form {
  &__label-text {
    &__title {
      color: $black;
    }

    &__success {
      color: $brand-green;
    }

    &__link {
      text-decoration: underline;
      color: $brand-orange;
      font-weight: 700 !important;
    }
  }

  &__error {
    margin-top: 0.5em;
  }
}

.discount-applied-display {
  background: $brand-blue;
  padding: 0.1em;
  border-radius: 0.3em;

  .content {
    border-radius: 0.2em;
    background: $white;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5em;
    text-align: left;

    svg {
      color: $grey-dark;
    }

    section {
      display: grid;
      place-items: start;
    }

    .title {
      color: $black;
      font-weight: $font-semi-bold;
      margin: 0;
    }

    h6 {
      color: $brand-dark-blue !important;
      font-size: $title-font-size-mob;
    }

    span {
      color: $brand-blue;
      font-size: $infobox-font-size;
    }
  }
}

button.btn {
  padding: 0.5em;
}

button.plain-btn {
  padding: 0.2em;
  background: $white;
  margin: 0;

  &:hover {
    background: $grey-mid;
  }
}

a {
  &:hover {
    cursor: pointer;
  }
}

.apply-discount-button {
  width: 60%;

  @include media("<medium") {
    width: 100%;
  }

  span {
    font-size: inherit;
    font-weight: inherit;
  }
}
</style>
